import { inject, InjectionToken } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppConfig } from '@shared/interfaces';
import { Destroyer } from '@shared/utils';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * iChopin service API
 */

export class ApiChopin extends Destroyer {
  private prefix;

  constructor(store: Store<{ config: AppConfig }>) {
    super();
    store.select('config')
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        this.prefix = `${ config.apiDomain || '' }${ config.apiPrefix || '' }`;
      });
  }

  LOGIN = ()  => `${this.prefix}/authentication_token`;
  REFRESH_TOKEN = () => `${this.prefix}/refresh_token`;
  PASSWORD_RESET = ()  => `${this.prefix}​/reset_password/reset`;
  PASSWORD_RESET_CHANGE = ()  => `${this.prefix}​/reset_password/change_password`;
  PEOPLE = (id?: number) => `${this.prefix}/people${ id ? ('/' + id) : '' }`;
  PEOPLE_SIMILAR = (personId: number) => `${this.prefix}/people/${ personId }/similar`;
  PEOPLE_FAVORITE = (id?: number) => `${this.prefix}/favorite_people${ id ? ('/' + id) : '' }`;
  EVENTS = (id?: number) => `${this.prefix}/events${ id ? ('/' + id) : '' }`;
  POSTS = (id?: number) => `${this.prefix}/posts${ id ? ('/' + id) : '' }`; // @TODO
  USER = (id?: number) => `${this.prefix}/users${ id ? ('/' + id) : '' }`;
  USER_ME = () => `${this.prefix}/users/me`;
  EDITIONS = (editionId?: number) => `${this.prefix}/editions${ editionId ? ('/' + editionId) : '' }`;
  SLIDES = (id?: number) => `${this.prefix}/slides${ id ? ('/' + id) : '' }`;
  EDITION_STAGES = (editionId?: number) => `${this.prefix}/editions${ editionId ? ('/' + editionId) : '' }/stages`;
  EDITION_STAGE = (stageId: number) => `${this.prefix}/stages/${ stageId }`;
  EDITION_STAGE_PARTICIPANTS = (stageId: number) => `${this.prefix}/stages/${ stageId }/participants`;
  EDITION_JURORS = (editionId: number) => `${this.prefix}/editions/${ editionId }/jurors`;
  EDITION_FINALISTS = (editionId: number) => `${this.prefix}/editions/${ editionId }/finalists`;
  COLLECTION = (id?: number) => `${this.prefix}/collections${ id ? ('/' + id) : '' }`;
  COLLECTION_PERFORMANCES = (collectionId: number) => `${this.prefix}/collections/${ collectionId }/collection_performances`;
  COLLECTION_REVIEWS = (collectionId: number) => `${this.prefix}/collections/${ collectionId }/reviews`;
  COLLECTION_AWARDS = (collectionId: number) => `${this.prefix}/collections/${ collectionId }/awards`;
  PERFORMANCE_FAVORITE = (id?: number) => `${this.prefix}/favorite_performances${ id ? ('/' + id) : '' }`;
  PERFORMANCE_FAVORITE_META = () => `${this.prefix}/favorite_performance_metadata`;
  PERFORMANCE_CHOSEN_FOR_USER = () => `${this.prefix}/performances/chosen_for_user`;
  PERFORMANCE = (id?: number) => `${this.prefix}/performances${ id ? ('/' + id) : '' }`;
  PERFORMANCE_META = () => `${this.prefix}/performance_metadata`;
  PERFORMANCE_CATEGORIES = () => `${this.prefix}/performance_categories`;
  RECENTLY_PLAYED = (id?: number) => `${this.prefix}/recently-played${ id ? ('/' + id) : '' }`;
  REVIEW = (id?: number) => `${this.prefix}/reviews${ id ? ('/' + id) : '' }`;
  AWARDS = (id?: number) => `${this.prefix}/awards${ id ? ('/' + id) : '' }`;
  PLAYLISTS = (id?: number) => `${this.prefix}/playlists${ id ? ('/' + id) : '' }`;
  PLAYLIST_ADD_PERFORMANCES = (id: number) => `${this.prefix}/playlists/${ id }/add-performances`;
  PLAYLIST_DELETE_PERFORMANCES = (id: number) => `${this.prefix}/playlists/${ id }/delete-performances`;
  PLAYLIST_PERFORMANCES = (id: number) => `${this.prefix}/playlists/${ id }/performances`;
  PAYMENT_MONTHLY = () => `${this.prefix}/payments/paypal/monthly_subscription`;
  PAYMENT_YEARLY = () => `${this.prefix}/payments/paypal/yearly_subscription`;
  PAYMENT_APPLE_STORE = () => `${this.prefix}/payments/apple_store`;
  PAYMENT_GOOGLE_STORE = () => `${this.prefix}/payments/google_store`;
  COUNTRIES = (id?: number) => `${this.prefix}/countries${ id ? ('/' + id) : '' }`;
  SEARCH = () => `${this.prefix}/search`;
  SEARCH_METADATA = () => `${this.prefix}/search_metadata`;
  SEARCH_PEOPLE = () => `${this.prefix}/search_people`;
  CONSENTS = () => `${this.prefix}/consents`;
  HOMEPAGE = () => `${this.prefix}/homepage`;
  TECH_MESSAGES = () => `${this.prefix}/technical_announcements`;
  TECH_MESSAGE_MARK_AS_READ = (id: number) => `${this.prefix}/technical_announcements/${ id }/mark_as_read`;
  SOCIAL_LOGIN_FB = () => `${this.prefix}/connect/facebook`;
  SOCIAL_LOGIN_GOOGLE = () => `${this.prefix}/connect/google`;
  SOCIAL_LOGIN_APPLE = () => `${this.prefix}/connect/apple`;
  CONFIG = () => environment.apiConfigUrl;
}

export const API_CHOPIN = new InjectionToken<ApiChopin>('API_CHOPIN', {
  providedIn: 'root',
  factory: (): ApiChopin => new ApiChopin(inject<Store<{ config: AppConfig }>>(Store)),
});
